// Under the bell (#512): a person switches on an email when new work waits on them and a daily digest of what waits
// and what is late, to an address they give. Both are off until they switch one on, and nothing goes to the address
// until they enter the code LAMP emailed to it (#655). Saving again sends no new code while one waits, so the code box
// shows only while one does; without one, saving gets one (#686), unless the person has had as many codes as LAMP sends
// in an hour: then the choice is saved and says the code comes on a save after (#718).
import { useEffect, useId, useState, type FormEvent } from "react";
import type { StaffNoticeChoice, StaffNoticeView } from "../api.js";
import { ApiError } from "../http.js";
import { useWords } from "../i18n/words.js";

export interface EmailChoiceApi {
  load(): Promise<StaffNoticeView>;
  choose(choice: StaffNoticeChoice): Promise<StaffNoticeView>;
  confirm(code: string): Promise<StaffNoticeView>;
}

export function EmailChoice({ api }: { api: EmailChoiceApi }) {
  const w = useWords();
  const [choice, setChoice] = useState<StaffNoticeView | undefined>(undefined);
  const [code, setCode] = useState("");
  const [email, setEmail] = useState("");
  const [newWork, setNewWork] = useState(false);
  const [digest, setDigest] = useState(false);
  const [said, setSaid] = useState<string | undefined>(undefined);
  const [busy, setBusy] = useState(false);
  const emailId = useId();
  const codeId = useId();

  /** The choice as the server keeps it now: after a refusal, it may still have been saved. */
  const reload = () =>
    api.load().then(
      (loaded) => setChoice(loaded),
      () => undefined,
    );
  useEffect(() => {
    api.load().then(
      (loaded) => {
        setChoice(loaded);
        setEmail(loaded.email ?? "");
        setNewWork(loaded.newWorkItems);
        setDigest(loaded.dailyDigest);
      },
      () => undefined,
    );
  }, [api]);
  // A Cell that emails no one about their work offers nothing here.
  if (!choice) return null;

  const save = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setSaid(undefined);
    try {
      const address = email.trim();
      const kept = await api.choose({
        email: address === "" ? null : address,
        newWorkItems: newWork,
        dailyDigest: digest,
      });
      // The same address with its code still waiting: no new code went.
      const waited = choice.codePending && choice.email === kept.email;
      setChoice(kept);
      setSaid(
        kept.email === null || kept.emailConfirmed
          ? w.shellEmailSaved
          : kept.codeHeld
            ? w.shellEmailCodeHeld
            : kept.codePending
              ? waited
                ? w.shellEmailCodeWaiting(kept.email)
                : w.shellEmailCodeSent(kept.email)
              : w.shellEmailSaveForCode,
      );
    } catch (error) {
      // The choice was kept, but the code could not be emailed.
      const noCode = error instanceof ApiError && error.problems.some((p) => p.startsWith("code:"));
      setSaid(noCode ? w.shellEmailCodeNotSent : w.shellEmailNeedsAddress);
      if (noCode) await reload();
    } finally {
      setBusy(false);
    }
  };

  const confirm = async () => {
    setBusy(true);
    setSaid(undefined);
    try {
      setChoice(await api.confirm(code.trim()));
      setCode("");
      setSaid(w.shellEmailConfirmed);
    } catch {
      setSaid(w.shellEmailWrongCode);
      // Tried too often, the code no longer waits.
      await reload();
    } finally {
      setBusy(false);
    }
  };
  // An address kept but not confirmed waits for the code emailed to it, while one is pending.
  const unconfirmed = choice.email !== null && !choice.emailConfirmed;
  const awaitingCode = unconfirmed && choice.codePending;

  return (
    <form className="email-choice" onSubmit={(event) => void save(event)}>
      <p className="menu-head small muted">{w.shellEmailMe}</p>
      <label className="check">
        <input type="checkbox" checked={newWork} onChange={(event) => setNewWork(event.target.checked)} />
        {w.shellEmailNewWork}
      </label>
      <label className="check">
        <input type="checkbox" checked={digest} onChange={(event) => setDigest(event.target.checked)} />
        {w.shellEmailDigest}
      </label>
      <label className="field" htmlFor={emailId}>
        {w.shellEmailAddress}
      </label>
      <input
        id={emailId}
        type="email"
        maxLength={254}
        value={email}
        required={newWork || digest}
        onChange={(event) => setEmail(event.target.value)}
      />
      <button type="submit" className="btn" disabled={busy}>
        {w.shellEmailSave}
      </button>
      {awaitingCode ? (
        <>
          <label className="field" htmlFor={codeId}>
            {w.shellEmailCode}
          </label>
          <input
            id={codeId}
            inputMode="numeric"
            autoComplete="one-time-code"
            maxLength={6}
            value={code}
            onChange={(event) => setCode(event.target.value)}
          />
          <button type="button" className="btn" disabled={busy || code.trim() === ""} onClick={() => void confirm()}>
            {w.shellEmailConfirm}
          </button>
        </>
      ) : unconfirmed && said === undefined ? (
        <p className="small muted">{w.shellEmailSaveForCode}</p>
      ) : null}
      {said ? <p role="status">{said}</p> : null}
    </form>
  );
}
